import { useState, useEffect, useCallback, useMemo } from "react";
import { usePageTitle } from "@/hooks/use-page-title";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import {
  Users, Eye, Globe, TrendingUp, TrendingDown, Monitor, Smartphone,
  ExternalLink, ArrowRight, KeyRound, X, ChevronsUpDown,
} from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

type Range = "7d" | "30d" | "90d" | "custom";
type SortDir = "asc" | "desc";
type FilterDim = "source" | "referrer" | "utm_source" | "utm_campaign" | "channel";
type ActiveFilter = { dim: FilterDim; value: string } | null;

const ORGANIC_SRC = ["google", "bing", "duckduckgo", "yahoo", "baidu", "yandex", "ecosia", "brave"].join("|");
const SOCIAL_SRC = ["twitter", "x", "facebook", "linkedin", "reddit", "instagram", "pinterest", "tiktok", "youtube", "threads"].join("|");
const PAID_MED = ["cpc", "paid", "ppc", "paidsearch", "paid_search", "cpm", "cpv"].join("|");

function buildPlausibleFilter(f: ActiveFilter): string | null {
  if (!f) return null;
  switch (f.dim) {
    case "source": return `visit:source==${f.value}`;
    case "referrer": return `visit:referrer==${f.value}`;
    case "utm_source": return `visit:utm_source==${f.value}`;
    case "utm_campaign": return `visit:utm_campaign==${f.value}`;
    case "channel":
      if (f.value === "Direct") return "visit:source==(direct)";
      if (f.value === "Organic Search") return `visit:source==${ORGANIC_SRC}`;
      if (f.value === "Paid Search") return `visit:utm_medium==${PAID_MED}`;
      if (f.value === "Social") return `visit:source==${SOCIAL_SRC}`;
      return null;
    default: return null;
  }
}

function filterLabel(f: ActiveFilter): string | null {
  if (!f) return null;
  const labels: Record<FilterDim, string> = { source: "Source", referrer: "Referrer", utm_source: "UTM Source", utm_campaign: "Campaign", channel: "Channel" };
  return `${labels[f.dim]}: ${f.value}`;
}

function fmt(n: number): string { return n.toLocaleString(); }
function fmtPct(n: number): string { return `${n.toFixed(1)}%`; }
function fmtSec(s: number): string {
  if (!s) return "0s";
  if (s < 60) return `${Math.round(s)}s`;
  return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
}
function delta(curr: number, prev: number | null): { pct: number; up: boolean } | null {
  if (prev === null || prev === undefined || prev === 0) return null;
  const pct = ((curr - prev) / prev) * 100;
  return { pct: Math.abs(pct), up: pct >= 0 };
}

function DeltaBadge({ curr, prev }: { curr: number; prev: number | null }) {
  const d = delta(curr, prev);
  if (!d) return null;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "0.2rem", fontSize: "0.75rem", fontWeight: 600, color: d.up ? "#10b981" : "#ef4444" }}>
      {d.up ? <TrendingUp style={{ width: "0.875rem", height: "0.875rem" }} /> : <TrendingDown style={{ width: "0.875rem", height: "0.875rem" }} />}
      {fmtPct(d.pct)}
    </span>
  );
}

function KpiCard({ title, value, sub, icon: Icon, color, prev }: { title: string; value: string | number; sub?: string; icon: React.ElementType; color: string; prev?: number | null }) {
  const numVal = typeof value === "number" ? value : null;
  return (
    <Card>
      <CardHeader style={{ paddingBottom: "0.375rem" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <CardTitle style={{ fontSize: "0.8125rem", fontWeight: 500, color: "hsl(var(--muted-foreground))" }}>{title}</CardTitle>
          <div style={{ width: "2.25rem", height: "2.25rem", borderRadius: "0.5rem", background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon style={{ width: "1.125rem", height: "1.125rem", color }} />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div style={{ fontSize: "1.75rem", fontWeight: 700 }}>{typeof value === "number" ? fmt(value) : value}</div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.25rem" }}>
          {sub && <p style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>{sub}</p>}
          {numVal !== null && prev !== undefined && <DeltaBadge curr={numVal} prev={prev ?? null} />}
        </div>
      </CardContent>
    </Card>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 style={{ fontSize: "1.0625rem", fontWeight: 700, marginBottom: "1rem", paddingBottom: "0.5rem", borderBottom: "1px solid hsl(var(--border))" }}>{children}</h2>;
}
function EmptyState({ msg }: { msg: string }) {
  return <div style={{ padding: "2rem", textAlign: "center", color: "hsl(var(--muted-foreground))", fontSize: "0.875rem" }}>{msg}</div>;
}
function SectionError({ msg }: { msg: string }) {
  return <div style={{ padding: "1rem", background: "hsl(var(--destructive) / 0.1)", borderRadius: "0.5rem", color: "hsl(var(--destructive))", fontSize: "0.875rem" }}>{msg}</div>;
}

function FilterChip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: "0.375rem", background: "#3b82f620", border: "1px solid #3b82f640", borderRadius: "9999px", padding: "0.25rem 0.5rem 0.25rem 0.75rem", fontSize: "0.8125rem", fontWeight: 500, color: "#3b82f6" }}>
      <span>Filter: {label}</span>
      <button onClick={onClear} style={{ display: "flex", alignItems: "center", background: "none", border: "none", cursor: "pointer", color: "#3b82f6", padding: "0.125rem" }}>
        <X style={{ width: "0.875rem", height: "0.875rem" }} />
      </button>
    </div>
  );
}

function NotConfiguredBanner({ siteId }: { siteId: string }) {
  return (
    <Card style={{ marginBottom: "2rem", borderColor: "#f59e0b", background: "#fffbeb" }}>
      <CardContent style={{ paddingTop: "1.25rem" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "0.875rem" }}>
          <KeyRound style={{ width: "1.5rem", height: "1.5rem", color: "#f59e0b", flexShrink: 0, marginTop: "0.125rem" }} />
          <div>
            <p style={{ fontWeight: 600, fontSize: "0.9375rem", marginBottom: "0.375rem", color: "#92400e" }}>Plausible API key not configured</p>
            <p style={{ fontSize: "0.875rem", color: "#92400e", lineHeight: 1.6 }}>
              Add your <strong>PLAUSIBLE_API_KEY</strong> secret in the Replit Secrets panel (lock icon in sidebar). Create an API key at{" "}
              <a href="https://plausible.io/settings/api-keys" target="_blank" rel="noreferrer" style={{ color: "#d97706", textDecoration: "underline" }}>plausible.io/settings/api-keys</a>.
              The site ID is currently <code style={{ fontFamily: "monospace", background: "#fde68a", padding: "0.1em 0.3em", borderRadius: "0.25rem" }}>{siteId}</code>.
            </p>
            <p style={{ fontSize: "0.8125rem", color: "#92400e", marginTop: "0.5rem" }}>
              <strong>Plausible goals to configure:</strong> Create two custom event goals named exactly{" "}
              <code style={{ fontFamily: "monospace", background: "#fde68a", padding: "0.1em 0.3em", borderRadius: "0.25rem" }}>Signup</code> and{" "}
              <code style={{ fontFamily: "monospace", background: "#fde68a", padding: "0.1em 0.3em", borderRadius: "0.25rem" }}>Activated</code> in your Plausible dashboard for the conversion funnel to work.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RangePicker({ range, setRange, customFrom, setCustomFrom, customTo, setCustomTo, compare, setCompare, onApply }: {
  range: Range; setRange: (r: Range) => void;
  customFrom: string; setCustomFrom: (s: string) => void;
  customTo: string; setCustomTo: (s: string) => void;
  compare: boolean; setCompare: (b: boolean) => void;
  onApply: () => void;
}) {
  const opts: { label: string; value: Range }[] = [
    { label: "Last 7 days", value: "7d" },
    { label: "Last 30 days", value: "30d" },
    { label: "Last 90 days", value: "90d" },
    { label: "Custom", value: "custom" },
  ];
  return (
    <Card style={{ marginBottom: "1.5rem" }}>
      <CardContent style={{ paddingTop: "1rem" }}>
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
          <span style={{ fontSize: "0.875rem", fontWeight: 500, marginRight: "0.25rem" }}>Date range:</span>
          {opts.map(opt => (
            <button key={opt.value} onClick={() => setRange(opt.value)} style={{
              padding: "0.375rem 0.875rem", borderRadius: "9999px", border: "1px solid",
              borderColor: range === opt.value ? "#3b82f6" : "hsl(var(--border))",
              background: range === opt.value ? "#3b82f6" : "transparent",
              color: range === opt.value ? "#fff" : "hsl(var(--foreground))",
              fontSize: "0.8125rem", cursor: "pointer", fontWeight: range === opt.value ? 600 : 400,
            }}>{opt.label}</button>
          ))}
          {range === "custom" && (
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} style={{ padding: "0.25rem 0.5rem", border: "1px solid hsl(var(--border))", borderRadius: "0.375rem", fontSize: "0.8125rem" }} />
              <span>to</span>
              <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} style={{ padding: "0.25rem 0.5rem", border: "1px solid hsl(var(--border))", borderRadius: "0.375rem", fontSize: "0.8125rem" }} />
              <button onClick={onApply} style={{ padding: "0.25rem 0.75rem", background: "#3b82f6", color: "#fff", border: "none", borderRadius: "0.375rem", cursor: "pointer", fontSize: "0.8125rem" }}>Apply</button>
            </div>
          )}
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.8125rem", cursor: "pointer" }}>
              <input type="checkbox" checked={compare} onChange={e => setCompare(e.target.checked)} style={{ cursor: "pointer" }} />
              Compare to previous period
            </label>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function buildQs(range: Range, from: string, to: string, extra?: Record<string, string>): string {
  return new URLSearchParams({ range, ...(range === "custom" ? { from, to } : {}), ...extra }).toString();
}

interface OverviewData {
  current: { results: { visitors: { value: number }; visits: { value: number }; pageviews: { value: number }; bounce_rate: { value: number }; visit_duration: { value: number } } };
  prev: { results: { visitors: { value: number }; visits: { value: number }; pageviews: { value: number }; bounce_rate: { value: number }; visit_duration: { value: number } } } | null;
}
interface TimeseriesRow { date: string; visitors: number }
interface TimeseriesData { results: TimeseriesRow[] }
interface ChannelInfo { visitors: number; rows: Array<{ source: string; visitors: number }> }
interface SourcesData {
  channels: Record<string, ChannelInfo>;
  prevChannels: Record<string, ChannelInfo> | null;
  referrers: { results: Array<{ referrer: string; visitors: number }> };
  utmSources: { results: Array<{ utm_source: string; visitors: number }> };
  utmMediums: { results: Array<{ utm_medium: string; visitors: number }> };
  utmCampaigns: { results: Array<{ utm_campaign: string; visitors: number }> };
  totalSiteVisitors: number;
}
interface FunnelData { visitors: number; landingPageViews: number; signups: number; activated: number; paid: number; paidFiltered: boolean }
interface PagesData {
  pages: { results: Array<{ entry_page: string; visitors: number; visits: number; bounce_rate: number; visit_duration: number }> };
  signupsByPage: { results: Array<{ entry_page: string; visitors: number }> };
}
interface CampaignRow { campaign: string; source: string; medium: string; visitors: number; signups: number }
interface CampaignsData { rows: CampaignRow[] }
interface DevicesData {
  devices: { results: Array<{ device: string; visitors: number }> };
  countries: { results: Array<{ country: string; visitors: number }> };
}

function useSection<T>(url: string): { data: T | null; loading: boolean; error: string } {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError("");
    fetch(`${BASE}${url}`, { credentials: "include" })
      .then(async res => {
        if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error ?? `HTTP ${res.status}`); }
        return res.json();
      })
      .then(d => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(e => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [url]);
  return { data, loading, error };
}

const CHANNEL_COLORS: Record<string, string> = { "Direct": "#6b7280", "Organic Search": "#3b82f6", "Paid Search": "#ef4444", "Social": "#8b5cf6", "Referral": "#f59e0b" };
const DEVICE_COLORS = ["#3b82f6", "#8b5cf6", "#10b981", "#f59e0b"];
const FUNNEL_COLORS = ["#3b82f6", "#6366f1", "#8b5cf6", "#06b6d4", "#10b981"];
const CHANNEL_ORDER = ["Direct", "Organic Search", "Paid Search", "Social", "Referral"];

function FunnelBar({ label, count, total, color, convRate, note }: { label: string; count: number; total: number; color: string; convRate?: number | null; note?: string }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "1rem", opacity: note ? 0.7 : 1 }}>
      <div style={{ width: "120px", fontSize: "0.8125rem", fontWeight: 500, flexShrink: 0, textAlign: "right" }}>
        {label}
        {note && <div style={{ fontSize: "0.6875rem", color: "#f59e0b", fontWeight: 400, lineHeight: 1.3 }}>{note}</div>}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ height: "28px", background: "hsl(var(--muted))", borderRadius: "0.375rem", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${pct}%`, background: note ? `${color}99` : color, borderRadius: "0.375rem", transition: "width 0.3s" }} />
          <div style={{ position: "absolute", left: "0.5rem", top: 0, bottom: 0, display: "flex", alignItems: "center", fontSize: "0.8125rem", fontWeight: 600, color: pct > 30 ? "#fff" : "hsl(var(--foreground))", zIndex: 1 }}>
            {fmt(count)}
          </div>
        </div>
      </div>
      <div style={{ width: "80px", fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", flexShrink: 0, textAlign: "right" }}>
        {fmtPct(pct)} of top
        {convRate !== null && convRate !== undefined && convRate > 0 && !note && (
          <div style={{ color: "#10b981", fontWeight: 600 }}>↑ {fmtPct(convRate)}</div>
        )}
      </div>
    </div>
  );
}

type SortKey = "campaign" | "source" | "medium" | "visitors" | "signups" | "convRate";

function SortBtn({ col, activeCol, dir, onClick }: { col: SortKey; activeCol: SortKey; dir: SortDir; onClick: () => void }) {
  const active = col === activeCol;
  return (
    <button onClick={onClick} style={{ display: "inline-flex", alignItems: "center", gap: "0.2rem", background: "none", border: "none", cursor: "pointer", fontWeight: active ? 700 : 600, color: active ? "#3b82f6" : "hsl(var(--muted-foreground))", fontSize: "0.8125rem", padding: 0 }}>
      {col.charAt(0).toUpperCase() + col.slice(1)}
      <ChevronsUpDown style={{ width: "0.75rem", height: "0.75rem", opacity: active ? 1 : 0.5 }} />
    </button>
  );
}

export default function AdminPublicAnalytics() {
  usePageTitle("Admin – Public Analytics");
  const [range, setRange] = useState<Range>("30d");
  const [compare, setCompare] = useState(false);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [liveCount, setLiveCount] = useState<number | null>(null);
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [siteId, setSiteId] = useState("quotrak.com");
  const [granularity, setGranularity] = useState<"day" | "week" | "month">("day");
  const [appliedRange, setAppliedRange] = useState<Range>("30d");
  const [appliedFrom, setAppliedFrom] = useState("");
  const [appliedTo, setAppliedTo] = useState("");
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>(null);
  const [campaignSort, setCampaignSort] = useState<{ col: SortKey; dir: SortDir }>({ col: "visitors", dir: "desc" });

  const handleApply = useCallback(() => {
    setAppliedRange(range);
    setAppliedFrom(customFrom);
    setAppliedTo(customTo);
  }, [range, customFrom, customTo]);

  useEffect(() => { if (range !== "custom") { setAppliedRange(range); setAppliedFrom(""); setAppliedTo(""); } }, [range]);

  useEffect(() => {
    fetch(`${BASE}/api/admin/analytics/public/config`, { credentials: "include" })
      .then(r => r.json())
      .then(d => { setConfigured(d.configured); setSiteId(d.siteId ?? "quotrak.com"); })
      .catch(() => setConfigured(false));
  }, []);

  useEffect(() => {
    if (!configured) return;
    let cancelled = false;
    const poll = () => {
      fetch(`${BASE}/api/admin/analytics/public/realtime`, { credentials: "include" })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (!cancelled && d != null) setLiveCount(typeof d === "number" ? d : d.count ?? 0); })
        .catch(() => {});
    };
    poll();
    const timer = window.setInterval(poll, 30_000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [configured]);

  const plausibleFilter = buildPlausibleFilter(activeFilter);
  const activeLabelText = filterLabel(activeFilter);
  const clearFilter = () => setActiveFilter(null);

  const qsBase = buildQs(appliedRange, appliedFrom, appliedTo);
  const qsCompare = buildQs(appliedRange, appliedFrom, appliedTo, compare ? { compare: "1" } : {});
  const filteredQsBase = plausibleFilter ? buildQs(appliedRange, appliedFrom, appliedTo, { filter: plausibleFilter }) : qsBase;
  const filteredQsCompare = plausibleFilter ? buildQs(appliedRange, appliedFrom, appliedTo, { filter: plausibleFilter, ...(compare ? { compare: "1" } : {}) }) : qsCompare;

  const overview = useSection<OverviewData>(`/api/admin/analytics/public/overview?${filteredQsCompare}`);
  const timeseries = useSection<TimeseriesData>(`/api/admin/analytics/public/timeseries?${filteredQsBase}&interval=${granularity}`);
  const sources = useSection<SourcesData>(`/api/admin/analytics/public/sources?${qsCompare}`);
  const funnel = useSection<FunnelData>(`/api/admin/analytics/public/funnel?${filteredQsBase}`);
  const pages = useSection<PagesData>(`/api/admin/analytics/public/pages?${filteredQsBase}`);
  const campaignsRaw = useSection<CampaignsData>(`/api/admin/analytics/public/campaigns?${qsBase}`);
  const devices = useSection<DevicesData>(`/api/admin/analytics/public/devices?${filteredQsBase}`);

  const isNotConfigured = (err: string) => err === "PLAUSIBLE_NOT_CONFIGURED";

  const curr = overview.data?.current?.results;
  const prev = overview.data?.prev?.results ?? null;

  const signupsByPageMap = new Map<string, number>((pages.data?.signupsByPage?.results ?? []).map(r => [r.entry_page, r.visitors]));

  const deviceRows = devices.data?.devices?.results ?? [];
  const totalDeviceVisitors = deviceRows.reduce((s, r) => s + r.visitors, 0);
  const countryRows = devices.data?.countries?.results ?? [];

  const funnelSteps: Array<{ label: string; count: number; note?: string }> = funnel.data ? [
    { label: "Visitors", count: funnel.data.visitors },
    { label: "Landing page visits", count: funnel.data.landingPageViews },
    { label: "Signup (free)", count: funnel.data.signups },
    { label: "Activated", count: funnel.data.activated },
    {
      label: "Paid user",
      count: funnel.data.paid,
      note: funnel.data.paidFiltered ? "DB total — not source-filtered" : undefined,
    },
  ] : [];

  const tsRows = timeseries.data?.results ?? [];

  const campaignRows = useMemo<CampaignRow[]>(() => {
    const rows = campaignsRaw.data?.rows ?? [];
    return [...rows].sort((a, b) => {
      const { col, dir } = campaignSort;
      let va: string | number, vb: string | number;
      if (col === "convRate") {
        va = a.visitors > 0 ? a.signups / a.visitors : 0;
        vb = b.visitors > 0 ? b.signups / b.visitors : 0;
      } else {
        va = a[col as keyof CampaignRow] as string | number;
        vb = b[col as keyof CampaignRow] as string | number;
      }
      if (typeof va === "string" && typeof vb === "string") return dir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      return dir === "asc" ? (va as number) - (vb as number) : (vb as number) - (va as number);
    });
  }, [campaignsRaw.data, campaignSort]);

  const toggleSort = (col: SortKey) => {
    setCampaignSort(prev => prev.col === col ? { col, dir: prev.dir === "desc" ? "asc" : "desc" } : { col, dir: "desc" });
  };

  return (
    <div style={{ maxWidth: "1200px" }}>
      <div style={{ marginBottom: "1.5rem", display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
        <div>
          <h1 style={{ fontSize: "1.75rem", fontWeight: 700, marginBottom: "0.25rem" }}>Public Analytics</h1>
          <p style={{ color: "hsl(var(--muted-foreground))", fontSize: "0.9375rem" }}>Marketing site traffic, acquisition, and conversion — powered by Plausible</p>
        </div>
        {liveCount !== null && configured && (
          <div style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", background: "#10b98120", color: "#10b981", padding: "0.375rem 0.875rem", borderRadius: "9999px", fontWeight: 600, fontSize: "0.9375rem" }}>
            <span style={{ width: "0.5rem", height: "0.5rem", borderRadius: "50%", background: "#10b981", display: "inline-block", animation: "pulse 2s infinite" }} />
            {liveCount} live {liveCount === 1 ? "visitor" : "visitors"} now
          </div>
        )}
      </div>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}} @keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>

      {configured === false && <NotConfiguredBanner siteId={siteId} />}
      {activeLabelText && (
        <div style={{ marginBottom: "1rem" }}>
          <FilterChip label={activeLabelText} onClear={clearFilter} />
        </div>
      )}

      <RangePicker range={range} setRange={setRange} customFrom={customFrom} setCustomFrom={setCustomFrom} customTo={customTo} setCustomTo={setCustomTo} compare={compare} setCompare={setCompare} onApply={handleApply} />

      {/* ── Traffic Overview ── */}
      <div style={{ marginBottom: "2rem" }}>
        <SectionTitle>Traffic Overview</SectionTitle>
        {overview.error && !isNotConfigured(overview.error) && <SectionError msg={overview.error} />}
        {isNotConfigured(overview.error) && <EmptyState msg="No data — add your Plausible API key to see traffic metrics." />}
        {!overview.error && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: "1rem", marginBottom: "1.5rem" }}>
              <KpiCard title="Unique Visitors" value={curr?.visitors?.value ?? 0} icon={Users} color="#3b82f6" sub="Distinct people" prev={compare ? (prev?.visitors?.value ?? null) : undefined} />
              <KpiCard title="Visits" value={curr?.visits?.value ?? 0} icon={Users} color="#6366f1" sub="Total sessions" prev={compare ? (prev?.visits?.value ?? null) : undefined} />
              <KpiCard title="Pageviews" value={curr?.pageviews?.value ?? 0} icon={Eye} color="#06b6d4" sub="Total page loads" prev={compare ? (prev?.pageviews?.value ?? null) : undefined} />
              <KpiCard title="Bounce Rate" value={`${(curr?.bounce_rate?.value ?? 0).toFixed(1)}%`} icon={Globe} color="#f59e0b" sub="Left without action" prev={compare ? (prev?.bounce_rate?.value ?? null) : undefined} />
              <KpiCard title="Avg. Visit Duration" value={fmtSec(curr?.visit_duration?.value ?? 0)} icon={TrendingUp} color="#10b981" sub="Time on site" prev={compare ? (prev?.visit_duration?.value ?? null) : undefined} />
            </div>
            <Card>
              <CardHeader>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "0.5rem" }}>
                  <CardTitle style={{ fontSize: "0.9375rem" }}>Visitors Over Time</CardTitle>
                  <div style={{ display: "flex", gap: "0.375rem" }}>
                    {(["day", "week", "month"] as const).map(iv => (
                      <button key={iv} onClick={() => setGranularity(iv)} style={{ padding: "0.25rem 0.625rem", borderRadius: "9999px", border: "1px solid", borderColor: granularity === iv ? "#3b82f6" : "hsl(var(--border))", background: granularity === iv ? "#3b82f6" : "transparent", color: granularity === iv ? "#fff" : "hsl(var(--foreground))", fontSize: "0.75rem", cursor: "pointer" }}>{iv}</button>
                    ))}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {timeseries.loading && <EmptyState msg="Loading…" />}
                {timeseries.error && !isNotConfigured(timeseries.error) && <SectionError msg={timeseries.error} />}
                {tsRows.length === 0 && !timeseries.loading && !timeseries.error && <EmptyState msg="No visitor data in this period." />}
                {tsRows.length > 0 && (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={tsRows.map(r => ({ date: r.date.slice(5), visitors: r.visitors }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip formatter={(v: number) => [fmt(v), "Visitors"]} />
                      <Line type="monotone" dataKey="visitors" stroke="#3b82f6" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* ── Acquisition Channels ── */}
      <div style={{ marginBottom: "2rem" }}>
        <SectionTitle>Acquisition Channels</SectionTitle>
        <p style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))", marginBottom: "1rem", marginTop: "-0.5rem" }}>Click a channel to filter the dashboard — overview, chart, funnel, pages, and devices all update.</p>
        {sources.error && !isNotConfigured(sources.error) && <SectionError msg={sources.error} />}
        {isNotConfigured(sources.error) && <EmptyState msg="No data — add your Plausible API key." />}
        {!sources.error && sources.loading && <EmptyState msg="Loading…" />}
        {!sources.error && sources.data && (() => {
          const channels = sources.data.channels;
          const prevChannels = sources.data.prevChannels;
          const totalVisitors = sources.data.totalSiteVisitors ?? Object.values(channels).reduce((s, c) => s + c.visitors, 0);
          return (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "1rem" }}>
              {CHANNEL_ORDER.map(name => {
                const info = channels[name] ?? { visitors: 0, rows: [] };
                const share = totalVisitors > 0 ? (info.visitors / totalVisitors) * 100 : 0;
                const prevVisitors = prevChannels?.[name]?.visitors ?? null;
                const isActiveChannel = activeFilter?.dim === "channel" && activeFilter.value === name;
                return (
                  <Card key={name} onClick={() => setActiveFilter({ dim: "channel", value: name })} style={{ cursor: "pointer", transition: "box-shadow 0.15s", outline: isActiveChannel ? "2px solid #3b82f6" : "none" }}>
                    <CardContent style={{ paddingTop: "1.25rem" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.375rem", marginBottom: "0.5rem" }}>
                        <span style={{ width: "0.625rem", height: "0.625rem", borderRadius: "50%", background: CHANNEL_COLORS[name] ?? "#6b7280", flexShrink: 0 }} />
                        <span style={{ fontSize: "0.8125rem", fontWeight: 500, color: "hsl(var(--muted-foreground))" }}>{name}</span>
                      </div>
                      <div style={{ fontSize: "1.5rem", fontWeight: 700 }}>{fmt(info.visitors)}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.25rem" }}>
                        <div style={{ height: "4px", flex: 1, background: "hsl(var(--muted))", borderRadius: "9999px" }}>
                          <div style={{ height: "4px", width: `${share}%`, background: CHANNEL_COLORS[name] ?? "#6b7280", borderRadius: "9999px", transition: "width 0.3s" }} />
                        </div>
                        <span style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", flexShrink: 0 }}>{fmtPct(share)}</span>
                      </div>
                      {compare && <DeltaBadge curr={info.visitors} prev={prevVisitors} />}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          );
        })()}
      </div>

      {/* ── Top Sources Drill-down ── */}
      <div style={{ marginBottom: "2rem" }}>
        <SectionTitle>Top Sources</SectionTitle>
        {sources.error && !isNotConfigured(sources.error) && <SectionError msg={sources.error} />}
        {isNotConfigured(sources.error) && <EmptyState msg="No data — add your Plausible API key." />}
        {!sources.error && sources.data && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
            <Card>
              <CardHeader><CardTitle style={{ fontSize: "0.9375rem" }}>Top Referrers</CardTitle></CardHeader>
              <CardContent>
                {(sources.data.referrers?.results ?? []).length === 0
                  ? <EmptyState msg="No referrer data yet." />
                  : (sources.data.referrers.results ?? []).slice(0, 10).map((r, i) => {
                    const max = sources.data!.referrers.results[0]?.visitors ?? 1;
                    const pct = Math.round((r.visitors / max) * 100);
                    return (
                      <div key={r.referrer + i} style={{ marginBottom: "0.75rem", cursor: "pointer" }} onClick={() => setActiveFilter({ dim: "referrer", value: r.referrer || "(direct)" })} title="Click to filter dashboard by this referrer">
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.2rem" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "0.375rem", minWidth: 0 }}>
                            <span style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", width: "1.25rem", flexShrink: 0 }}>{i + 1}</span>
                            <ExternalLink style={{ width: "0.75rem", height: "0.75rem", color: "hsl(var(--muted-foreground))", flexShrink: 0 }} />
                            <span style={{ fontSize: "0.8125rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.referrer}>{r.referrer || "(direct)"}</span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0 }}>
                            <span style={{ fontSize: "0.8125rem", fontWeight: 600 }}>{fmt(r.visitors)}</span>
                            <ArrowRight style={{ width: "0.75rem", height: "0.75rem", color: "#3b82f6" }} />
                          </div>
                        </div>
                        <div style={{ height: "4px", background: "hsl(var(--muted))", borderRadius: "9999px" }}>
                          <div style={{ height: "4px", background: "#3b82f6", borderRadius: "9999px", width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
              </CardContent>
            </Card>

            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <Card>
                <CardHeader><CardTitle style={{ fontSize: "0.9375rem" }}>UTM Sources</CardTitle></CardHeader>
                <CardContent>
                  {(sources.data.utmSources?.results ?? []).length === 0
                    ? <EmptyState msg="No UTM source data yet." />
                    : (sources.data.utmSources.results ?? []).slice(0, 6).map((r, i) => (
                      <div key={i} onClick={() => setActiveFilter({ dim: "utm_source", value: r.utm_source })} style={{ display: "flex", justifyContent: "space-between", padding: "0.375rem 0", borderBottom: "1px solid hsl(var(--border))", fontSize: "0.8125rem", cursor: "pointer" }}>
                        <span style={{ color: "hsl(var(--muted-foreground))" }}>{r.utm_source || "(none)"}</span>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                          <span style={{ fontWeight: 600 }}>{fmt(r.visitors)}</span>
                          <ArrowRight style={{ width: "0.75rem", height: "0.75rem", color: "#3b82f6" }} />
                        </div>
                      </div>
                    ))}
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle style={{ fontSize: "0.9375rem" }}>UTM Mediums</CardTitle></CardHeader>
                <CardContent>
                  {(sources.data.utmMediums?.results ?? []).length === 0
                    ? <EmptyState msg="No UTM medium data yet." />
                    : (sources.data.utmMediums.results ?? []).slice(0, 6).map((r, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "0.375rem 0", borderBottom: "1px solid hsl(var(--border))", fontSize: "0.8125rem" }}>
                        <span style={{ color: "hsl(var(--muted-foreground))" }}>{r.utm_medium || "(none)"}</span>
                        <span style={{ fontWeight: 600 }}>{fmt(r.visitors)}</span>
                      </div>
                    ))}
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle style={{ fontSize: "0.9375rem" }}>UTM Campaigns</CardTitle></CardHeader>
                <CardContent>
                  {(sources.data.utmCampaigns?.results ?? []).length === 0
                    ? <EmptyState msg="No UTM campaign data yet." />
                    : (sources.data.utmCampaigns.results ?? []).slice(0, 6).map((r, i) => (
                      <div key={i} onClick={() => setActiveFilter({ dim: "utm_campaign", value: r.utm_campaign })} style={{ display: "flex", justifyContent: "space-between", padding: "0.375rem 0", borderBottom: "1px solid hsl(var(--border))", fontSize: "0.8125rem", cursor: "pointer" }}>
                        <span style={{ color: "hsl(var(--muted-foreground))" }}>{r.utm_campaign || "(none)"}</span>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                          <span style={{ fontWeight: 600 }}>{fmt(r.visitors)}</span>
                          <ArrowRight style={{ width: "0.75rem", height: "0.75rem", color: "#3b82f6" }} />
                        </div>
                      </div>
                    ))}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>

      {/* ── Conversion Funnel ── */}
      <div style={{ marginBottom: "2rem" }}>
        <SectionTitle>
          Conversion Funnel
          {activeLabelText && <span style={{ fontSize: "0.8125rem", fontWeight: 400, color: "#3b82f6", marginLeft: "0.75rem" }}>({activeLabelText})</span>}
        </SectionTitle>
        {funnel.error && !isNotConfigured(funnel.error) && <SectionError msg={funnel.error} />}
        {isNotConfigured(funnel.error) && <EmptyState msg="No data — add your Plausible API key." />}
        {!funnel.error && funnel.loading && <EmptyState msg="Loading…" />}
        {!funnel.error && funnel.data && (
          <Card>
            <CardContent style={{ paddingTop: "1.5rem" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                {funnelSteps.map((step, i) => {
                  const convRate = i > 0 && funnelSteps[i - 1].count > 0 ? (step.count / funnelSteps[i - 1].count) * 100 : null;
                  return (
                    <div key={step.label}>
                      {i > 0 && convRate !== null && (
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginLeft: "136px", marginBottom: "0.25rem" }}>
                          <ArrowRight style={{ width: "0.875rem", height: "0.875rem", color: "hsl(var(--muted-foreground))" }} />
                          <span style={{ fontSize: "0.75rem", color: convRate > 20 ? "#10b981" : convRate > 5 ? "#f59e0b" : "#ef4444", fontWeight: 600 }}>
                            {fmtPct(convRate)} conversion from previous step
                          </span>
                        </div>
                      )}
                      <FunnelBar label={step.label} count={step.count} total={funnelSteps[0].count} color={FUNNEL_COLORS[i]} note={step.note} />
                    </div>
                  );
                })}
              </div>
              {funnel.data.signups === 0 && funnel.data.activated === 0 && (
                <p style={{ marginTop: "1rem", fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))", borderTop: "1px solid hsl(var(--border))", paddingTop: "1rem" }}>
                  Signup and Activated counts populate once those goals are configured in Plausible and the custom events are firing.
                </p>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── Top Landing Pages ── */}
      <div style={{ marginBottom: "2rem" }}>
        <SectionTitle>Top Landing Pages</SectionTitle>
        {pages.error && !isNotConfigured(pages.error) && <SectionError msg={pages.error} />}
        {isNotConfigured(pages.error) && <EmptyState msg="No data — add your Plausible API key." />}
        {!pages.error && pages.data && (
          <Card>
            <CardContent style={{ paddingTop: 0 }}>
              {(pages.data.pages?.results ?? []).length === 0
                ? <EmptyState msg="No entry page data yet." />
                : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8125rem" }}>
                      <thead>
                        <tr style={{ borderBottom: "1px solid hsl(var(--border))" }}>
                          {["Page", "Visitors", "Visits", "Bounce Rate", "Avg. Duration", "Signups"].map(h => (
                            <th key={h} style={{ textAlign: h === "Page" ? "left" : "right", padding: "0.75rem", fontWeight: 600, color: "hsl(var(--muted-foreground))" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(pages.data.pages.results ?? []).map((row, i) => {
                          const sups = signupsByPageMap.get(row.entry_page) ?? 0;
                          // Conversion rate denominator = visits (entry sessions for this page) per task spec
                          const denom = row.visits ?? row.visitors;
                          const convRate = denom > 0 ? (sups / denom) * 100 : 0;
                          return (
                            <tr key={i} style={{ borderBottom: "1px solid hsl(var(--border))" }}>
                              <td style={{ padding: "0.625rem 0.75rem", fontFamily: "monospace", maxWidth: "260px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.entry_page}>{row.entry_page}</td>
                              <td style={{ padding: "0.625rem 0.75rem", textAlign: "right", fontWeight: 600 }}>{fmt(row.visitors)}</td>
                              <td style={{ padding: "0.625rem 0.75rem", textAlign: "right" }}>{fmt(row.visits ?? 0)}</td>
                              <td style={{ padding: "0.625rem 0.75rem", textAlign: "right" }}>{fmtPct(row.bounce_rate ?? 0)}</td>
                              <td style={{ padding: "0.625rem 0.75rem", textAlign: "right" }}>{fmtSec(row.visit_duration ?? 0)}</td>
                              <td style={{ padding: "0.625rem 0.75rem", textAlign: "right" }}>
                                {sups > 0 ? (<span>{fmt(sups)} <span style={{ fontSize: "0.75rem", color: "#10b981" }}>({fmtPct(convRate)})</span></span>) : "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── Campaign Tracking ── */}
      <div style={{ marginBottom: "2rem" }}>
        <SectionTitle>Campaign Tracking (UTM)</SectionTitle>
        {campaignsRaw.error && !isNotConfigured(campaignsRaw.error) && <SectionError msg={campaignsRaw.error} />}
        {isNotConfigured(campaignsRaw.error) && <EmptyState msg="No data — add your Plausible API key." />}
        {!campaignsRaw.error && campaignsRaw.data && (
          <Card>
            <CardContent style={{ paddingTop: 0 }}>
              {campaignRows.length === 0
                ? <EmptyState msg="No UTM campaign data yet. Add utm_campaign parameters to your links." />
                : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8125rem" }}>
                      <thead>
                        <tr style={{ borderBottom: "1px solid hsl(var(--border))" }}>
                          <th style={{ textAlign: "left", padding: "0.75rem" }}><SortBtn col="campaign" activeCol={campaignSort.col} dir={campaignSort.dir} onClick={() => toggleSort("campaign")} /></th>
                          <th style={{ textAlign: "left", padding: "0.75rem" }}><SortBtn col="source" activeCol={campaignSort.col} dir={campaignSort.dir} onClick={() => toggleSort("source")} /></th>
                          <th style={{ textAlign: "left", padding: "0.75rem" }}><SortBtn col="medium" activeCol={campaignSort.col} dir={campaignSort.dir} onClick={() => toggleSort("medium")} /></th>
                          <th style={{ textAlign: "right", padding: "0.75rem" }}><SortBtn col="visitors" activeCol={campaignSort.col} dir={campaignSort.dir} onClick={() => toggleSort("visitors")} /></th>
                          <th style={{ textAlign: "right", padding: "0.75rem" }}><SortBtn col="signups" activeCol={campaignSort.col} dir={campaignSort.dir} onClick={() => toggleSort("signups")} /></th>
                          <th style={{ textAlign: "right", padding: "0.75rem" }}><SortBtn col="convRate" activeCol={campaignSort.col} dir={campaignSort.dir} onClick={() => toggleSort("convRate")} /></th>
                        </tr>
                      </thead>
                      <tbody>
                        {campaignRows.map((row, i) => {
                          const conv = row.visitors > 0 ? (row.signups / row.visitors) * 100 : 0;
                          return (
                            <tr key={i} style={{ borderBottom: "1px solid hsl(var(--border))", cursor: "pointer" }} onClick={() => setActiveFilter({ dim: "utm_campaign", value: row.campaign })} title="Click to filter dashboard by this campaign">
                              <td style={{ padding: "0.625rem 0.75rem", fontWeight: 500 }}>{row.campaign || "(none)"}</td>
                              <td style={{ padding: "0.625rem 0.75rem", color: "hsl(var(--muted-foreground))" }}>{row.source || "—"}</td>
                              <td style={{ padding: "0.625rem 0.75rem", color: "hsl(var(--muted-foreground))" }}>{row.medium || "—"}</td>
                              <td style={{ padding: "0.625rem 0.75rem", textAlign: "right", fontWeight: 600 }}>{fmt(row.visitors)}</td>
                              <td style={{ padding: "0.625rem 0.75rem", textAlign: "right" }}>{row.signups > 0 ? fmt(row.signups) : "—"}</td>
                              <td style={{ padding: "0.625rem 0.75rem", textAlign: "right" }}>
                                {row.signups > 0 ? <span style={{ color: "#10b981", fontWeight: 600 }}>{fmtPct(conv)}</span> : "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── Device + Location ── */}
      <div style={{ marginBottom: "2rem" }}>
        <SectionTitle>Device &amp; Location</SectionTitle>
        {devices.error && !isNotConfigured(devices.error) && <SectionError msg={devices.error} />}
        {isNotConfigured(devices.error) && <EmptyState msg="No data — add your Plausible API key." />}
        {!devices.error && devices.data && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
            <Card>
              <CardHeader><CardTitle style={{ fontSize: "0.9375rem" }}>Device Type</CardTitle></CardHeader>
              <CardContent>
                {deviceRows.length === 0 ? <EmptyState msg="No device data yet." /> : (
                  <div style={{ display: "flex", alignItems: "center", gap: "1.5rem", flexWrap: "wrap" }}>
                    <ResponsiveContainer width={180} height={180}>
                      <PieChart>
                        <Pie data={deviceRows.map(r => ({ name: r.device, value: r.visitors }))} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value">
                          {deviceRows.map((_, i) => <Cell key={i} fill={DEVICE_COLORS[i % DEVICE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(v: number) => [fmt(v), "Visitors"]} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.625rem" }}>
                      {deviceRows.map((r, i) => {
                        const pct = totalDeviceVisitors > 0 ? (r.visitors / totalDeviceVisitors) * 100 : 0;
                        return (
                          <div key={r.device} style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.875rem" }}>
                            <span style={{ width: "0.625rem", height: "0.625rem", borderRadius: "50%", background: DEVICE_COLORS[i % DEVICE_COLORS.length], flexShrink: 0 }} />
                            <span style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                              {r.device === "Desktop" ? <Monitor style={{ width: "0.875rem", height: "0.875rem" }} /> : <Smartphone style={{ width: "0.875rem", height: "0.875rem" }} />}
                              {r.device}
                            </span>
                            <span style={{ fontWeight: 600 }}>{fmtPct(pct)}</span>
                            <span style={{ color: "hsl(var(--muted-foreground))", fontSize: "0.75rem" }}>({fmt(r.visitors)})</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle style={{ fontSize: "0.9375rem" }}>Top Countries</CardTitle></CardHeader>
              <CardContent>
                {countryRows.length === 0 ? <EmptyState msg="No country data yet." /> : countryRows.map((r, i) => {
                  const max = countryRows[0]?.visitors ?? 1;
                  const pct = Math.round((r.visitors / max) * 100);
                  return (
                    <div key={r.country} style={{ marginBottom: "0.75rem" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.2rem" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}>
                          <span style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", width: "1.25rem" }}>{i + 1}</span>
                          <span style={{ fontSize: "0.8125rem" }}>{r.country}</span>
                        </div>
                        <span style={{ fontSize: "0.8125rem", fontWeight: 600 }}>{fmt(r.visitors)}</span>
                      </div>
                      <div style={{ height: "4px", background: "hsl(var(--muted))", borderRadius: "9999px" }}>
                        <div style={{ height: "4px", background: "#06b6d4", borderRadius: "9999px", width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
